import React from "react";
import ReactDatePicker from "react-datepicker";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css";
import { Input } from "./Field";
import Button from "./Button";

/**
 * Bun date picker: a token skin over react-datepicker (already a dependency;
 * we do not build calendars from scratch). Spec: Component Library "Bottom
 * sheet, toasts & date picker" — presets on top, ink range endpoints,
 * mist-deep in-range band, 1.5px ink ring on today.
 *
 * Accepts every react-datepicker prop (selected/onChange, selectsRange +
 * startDate/endDate, inline, minDate, ...). Additions:
 *   presets  [{ label, onSelect, active }] — pill row above the month nav.
 *   footer   node rendered below the grid (range summary + Apply button).
 *
 *   <DatePicker inline selectsRange startDate={s} endDate={e} onChange={setRange}
 *     presets={[{ label: "This month", active: true, onSelect: ... }, ...]}
 *     footer={<><span className="flex-1 text-bun-meta font-book text-bun-stone">Aug 10 - Aug 15</span>
 *             <Button size="sm">Apply</Button></>} />
 */
export default function DatePicker({
  presets,
  footer,
  className,
  children,
  nativeOnMobile = false,
  mobileLabel = 'Date',
  ...props
}) {
  const renderHeader = ({ monthDate, decreaseMonth, increaseMonth }) => (
    <div className="font-instrument">
      {presets && presets.length > 0 && (
        <div className="flex flex-wrap gap-[5px] mb-3">
          {presets.map((preset) => (
            <Button
              key={preset.label}
              variant={preset.active ? 'primary' : 'secondary'}
              size="sm"
              shape="round"
              onClick={preset.onSelect}
              aria-pressed={preset.active}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <Button variant="secondary" size="sm" iconOnly aria-label="Previous month" onClick={decreaseMonth}>
          <ChevronLeftIcon className="w-[11px] h-[11px]" aria-hidden="true" />
        </Button>
        <span className="text-bun-body font-semibold text-bun-text">
          {monthDate.toLocaleString("default", { month: "long", year: "numeric" })}
        </span>
        <Button variant="secondary" size="sm" iconOnly aria-label="Next month" onClick={increaseMonth}>
          <ChevronRightIcon className="w-[11px] h-[11px]" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );

  const calendar = (
    <ReactDatePicker
      customInput={<Input />}
      showPopperArrow={false}
      renderCustomHeader={renderHeader}
      wrapperClassName={className}
      {...props}
      calendarClassName="bun-datepicker"
      popperClassName="bun-datepicker-popper"
    >
      {footer && <div className="flex items-center gap-2 font-instrument">{footer}</div>}
      {children}
    </ReactDatePicker>
  );
  if (!nativeOnMobile) return calendar;
  // Single-date fields use the device picker on small screens so the calendar
  // does not overflow an inset card or compress day targets below 44px.
  const dateValue = (date) => date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : '';
  return <>
    <div className="sm:hidden"><Input type="date" aria-label={mobileLabel}
      value={dateValue(props.selected)} min={dateValue(props.minDate)} max={dateValue(props.maxDate)} disabled={props.disabled}
      onChange={event => props.onChange(event.target.value ? new Date(`${event.target.value}T00:00:00`) : null)} /></div>
    <div className="hidden sm:block">{calendar}</div>
  </>;
}
